/**
 * AudioRecorder Component
 * Thin UI layer over AddContext — supports both batch and streaming modes.
 *
 * In streaming mode:
 * - Shows live transcript panel below recording controls
 * - Partial text in italic/lighter weight, finalized text in normal weight
 * - Connection status indicator (green/yellow/red dot)
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Pause, Play, AlertCircle, CheckCircle, ArrowRight, Trash2, Wifi, WifiOff } from 'lucide-react';
import { useAdd } from '@/contexts/AddContext';
import { Button } from '@/components/ui';

/** Max words to render in the visible transcript panel. Full text is kept in hook state. */
const VISIBLE_WORD_LIMIT = 200;

interface AudioRecorderProps {
  isDisabled?: boolean;
  recordingLanguage?: string;
  onStreamingSubmit?: () => void;
  streamingCaptured?: boolean;
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({
  isDisabled = false,
  recordingLanguage = 'en-US',
  onStreamingSubmit,
  streamingCaptured = false,
}) => {
  const { t } = useTranslation();
  const {
    recordingState,
    duration,
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    discardRecording,
    getAnalyserNode,
    maxDurationSeconds,
    transcriptionMode,
    streaming,
  } = useAdd();

  const [audioLevel, setAudioLevel] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const transcriptPanelRef = useRef<HTMLDivElement>(null);

  // Determine which mode is active
  const isStreamingMode = transcriptionMode === 'streaming';
  // When streaming is captured, treat it as idle for display purposes
  const isStreamingActive = isStreamingMode && !streamingCaptured && (
    streaming.sessionStatus === 'active' ||
    streaming.sessionStatus === 'paused' ||
    streaming.sessionStatus === 'connecting'
  );
  const isStreamingIdle = isStreamingMode && (
    streamingCaptured ||
    streaming.sessionStatus === 'idle' ||
    streaming.sessionStatus === 'error' ||
    streaming.sessionStatus === 'stopped'
  );

  // Unified state for display
  const currentDuration = isStreamingActive ? streaming.duration : duration;
  const currentState = isStreamingActive
    ? (streaming.sessionStatus === 'active' || streaming.sessionStatus === 'connecting' ? 'recording' : streaming.sessionStatus === 'paused' ? 'paused' : 'idle')
    : recordingState;
  const currentGetAnalyser = isStreamingActive ? streaming.getAnalyserNode : getAnalyserNode;

  // Audio level visualization
  const updateAudioLevel = useCallback(() => {
    const analyser = currentGetAnalyser();
    if (!analyser) return;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);

    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    setAudioLevel(average / 255);

    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  }, [currentGetAnalyser]);

  useEffect(() => {
    if (currentState === 'recording') {
      updateAudioLevel();
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      setAudioLevel(0);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [currentState, updateAudioLevel]);

  // Auto-scroll transcript panel
  useEffect(() => {
    if (transcriptPanelRef.current) {
      transcriptPanelRef.current.scrollTop = transcriptPanelRef.current.scrollHeight;
    }
  }, [streaming.finalizedTranscript, streaming.liveTranscript]);

  // Truncate visible transcript to last N words for DOM performance
  const { visibleTranscript, totalWordCount, isTruncated } = useMemo(() => {
    const full = streaming.finalizedTranscript || '';
    const words = full.split(/\s+/).filter(Boolean);
    const total = words.length;
    if (total <= VISIBLE_WORD_LIMIT) {
      return { visibleTranscript: full, totalWordCount: total, isTruncated: false };
    }
    const truncated = words.slice(-VISIBLE_WORD_LIMIT).join(' ');
    return { visibleTranscript: truncated, totalWordCount: total, isTruncated: true };
  }, [streaming.finalizedTranscript]);

  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start handler - uses streaming or batch depending on mode
  const handleStart = useCallback(async () => {
    if (isStreamingMode) {
      await streaming.startStreaming(recordingLanguage);
    } else {
      await startRecording(recordingLanguage);
    }
  }, [isStreamingMode, streaming, recordingLanguage, startRecording]);

  // Capture handler — finalises the current recording into the Captured Items list
  const handleStop = useCallback(() => {
    if (isStreamingMode) {
      // Pause streaming if still active (no-op if already paused)
      if (streaming.sessionStatus === 'active' || streaming.sessionStatus === 'connecting') {
        streaming.pauseStreaming();
      }
      // Signal to parent that the recording is captured (adds to Captured Items list)
      onStreamingSubmit?.();
    } else {
      stopRecording();
    }
  }, [isStreamingMode, streaming, stopRecording, onStreamingSubmit]);

  // Pause handler
  const handlePause = useCallback(() => {
    if (isStreamingMode) {
      streaming.pauseStreaming();
    } else {
      pauseRecording();
    }
  }, [isStreamingMode, streaming, pauseRecording]);

  // Resume handler
  const handleResume = useCallback(async () => {
    if (isStreamingMode) {
      await streaming.resumeStreaming();
    } else {
      resumeRecording();
    }
  }, [isStreamingMode, streaming, resumeRecording]);

  // Discard handler
  const handleDiscard = useCallback(() => {
    if (isStreamingMode) {
      streaming.discardStreaming();
    } else {
      discardRecording();
    }
  }, [isStreamingMode, streaming, discardRecording]);

  const isIdle = isStreamingMode ? isStreamingIdle : recordingState === 'idle';

  // Status message when streaming recording has been captured
  const showCapturedMessage = isStreamingMode && streamingCaptured;

  return (
    <div className="w-full">
      <div className={`
        relative border-2 rounded-xl p-4 sm:p-8 text-center
        transition-all duration-200
        ${isDisabled
          ? 'bg-slate-100 border-slate-300 opacity-60'
          : currentState === 'recording'
            ? 'bg-red-50 border-red-400'
            : currentState === 'paused'
              ? 'bg-amber-50 border-amber-400'
              : 'bg-slate-50 border-slate-300'
        }
      `}>
        {/* Audio Level Indicator */}
        {currentState === 'recording' && (
          <div className="absolute top-4 left-4 right-4 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-red-500 transition-all duration-100"
              style={{ width: `${audioLevel * 100}%` }}
            />
          </div>
        )}

        {/* Connection Status (streaming mode only) */}
        {isStreamingMode && !isIdle && (
          <div className="absolute top-4 right-4 flex items-center gap-1.5 text-xs">
            {streaming.isTranscribeConnected && streaming.isWebSocketConnected ? (
              <>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <Wifi className="w-3 h-3 text-emerald-600" />
              </>
            ) : streaming.isWebSocketConnected || streaming.isTranscribeConnected ? (
              <>
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <Wifi className="w-3 h-3 text-amber-600" />
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <WifiOff className="w-3 h-3 text-red-600" />
              </>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="mt-4">
          {/* Microphone Icon - only show when recording or paused */}
          {!isIdle && (
            <div className={`
              w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center
              transition-all duration-200
              ${currentState === 'recording'
                ? 'bg-red-100 animate-pulse'
                : currentState === 'paused'
                  ? 'bg-amber-100'
                  : 'bg-indigo-100'
              }
            `}>
              <Mic className={`
                w-10 h-10
                ${currentState === 'recording'
                  ? 'text-red-600'
                  : currentState === 'paused'
                    ? 'text-amber-600'
                    : 'text-indigo-600'
                }
              `} />
            </div>
          )}

          {/* Duration */}
          <p className={`
            text-3xl font-mono font-bold mb-4
            ${currentState === 'recording' ? 'text-red-600' : 'text-slate-700'}
          `}>
            {formatDuration(currentDuration)}
          </p>

          {/* Status Text */}
          <p className="text-sm text-slate-500 mb-6">
            {isIdle && !showCapturedMessage && t('recorder.tapToStart')}
            {showCapturedMessage && t('recorder.recordingCapturedToList')}
            {currentState === 'recording' && (
              isStreamingMode ? t('recorder.streamingInProgress') : t('recorder.recordingInProgress')
            )}
            {currentState === 'paused' && t('recorder.recordingPaused')}
            {currentState === 'stopped' && t('recorder.recordingComplete')}
          </p>

          {/* Controls */}
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            {isIdle && (
              <button
                onClick={handleStart}
                disabled={isDisabled || streamingCaptured}
                className="group relative transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                aria-label={t('recorder.startRecording')}
              >
                <img
                  src="/record_button.png"
                  alt={t('recorder.startRecording')}
                  className="w-32 h-32 object-contain drop-shadow-lg group-hover:drop-shadow-xl transition-all"
                />
              </button>
            )}

            {currentState === 'recording' && (
              <>
                <button
                  onClick={handlePause}
                  className="p-3 sm:px-4 sm:py-3 bg-amber-500 text-white rounded-xl font-medium
                           hover:bg-amber-600 transition-colors flex items-center gap-2"
                  aria-label={t('recorder.pause')}
                >
                  <Pause className="w-5 h-5" />
                  <span className="hidden sm:inline">{t('recorder.pause')}</span>
                </button>
                <Button
                  onClick={handleStop}
                  variant="gradient"
                  size="lg"
                  shape="rounded"
                  aria-label={t('recorder.captureRecording')}
                >
                  <ArrowRight className="w-5 h-5" />
                  <span className="hidden sm:inline">{t('recorder.captureRecording')}</span>
                </Button>
                <button
                  onClick={handleDiscard}
                  className="p-3 sm:px-4 sm:py-3 bg-slate-200 text-slate-600 rounded-xl font-medium
                           hover:bg-slate-300 transition-colors flex items-center gap-2"
                  aria-label={t('recorder.discard')}
                >
                  <Trash2 className="w-5 h-5" />
                  <span className="hidden sm:inline">{t('recorder.discard')}</span>
                </button>
              </>
            )}

            {currentState === 'paused' && (
              <>
                <button
                  onClick={handleResume}
                  className="p-3 sm:px-4 sm:py-3 bg-amber-500 text-white rounded-xl font-medium
                           hover:bg-amber-600 transition-colors flex items-center gap-2"
                  aria-label={t('recorder.resume')}
                >
                  <Play className="w-5 h-5" />
                  <span className="hidden sm:inline">{t('recorder.resume')}</span>
                </button>
                <Button
                  onClick={handleStop}
                  variant="gradient"
                  size="lg"
                  shape="rounded"
                  aria-label={t('recorder.captureRecording')}
                >
                  <ArrowRight className="w-5 h-5" />
                  <span className="hidden sm:inline">{t('recorder.captureRecording')}</span>
                </Button>
                <button
                  onClick={handleDiscard}
                  className="p-3 sm:px-4 sm:py-3 bg-slate-200 text-slate-600 rounded-xl font-medium
                           hover:bg-slate-300 transition-colors flex items-center gap-2"
                  aria-label={t('recorder.discard')}
                >
                  <Trash2 className="w-5 h-5" />
                  <span className="hidden sm:inline">{t('recorder.discard')}</span>
                </button>
              </>
            )}

            {currentState === 'stopped' && (
              <div className="flex items-center gap-2 text-indigo-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">{t('recorder.recordingCompleteProcessing')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Max Duration Warning */}
        {!isIdle && maxDurationSeconds < 86400 && currentDuration >= maxDurationSeconds - 60 && (
          <p className="mt-4 text-sm text-amber-600">
            {t('recorder.autoStopWarning', { time: formatDuration(maxDurationSeconds - currentDuration) })}
          </p>
        )}
      </div>

      {/* Live Transcript Panel (streaming mode only) */}
      {isStreamingMode && !isIdle && (
        <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-700">{t('recorder.liveTranscript')}</h4>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              {totalWordCount > 0 && (
                <span>{totalWordCount} {t('recorder.words')}</span>
              )}
              <span>
                {streaming.transcriptFragments.length} {t('recorder.fragments')}
              </span>
            </div>
          </div>
          <div
            ref={transcriptPanelRef}
            className="max-h-48 overflow-y-auto text-sm text-slate-800 leading-relaxed"
          >
            {isTruncated && (
              <span className="text-slate-400">&hellip; </span>
            )}
            {visibleTranscript && (
              <span>{visibleTranscript}</span>
            )}
            {streaming.liveTranscript && (
              <span className="text-slate-400 italic"> {streaming.liveTranscript}</span>
            )}
            {!visibleTranscript && !streaming.liveTranscript && (
              <span className="text-slate-400 italic">{t('recorder.listeningForSpeech')}</span>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
